/* ============================================================
   SORVEN — CART BEHAVIOUR
   ------------------------------------------------------------
   Four jobs, all of them layered on top of Shrine's cart rather
   than replacing any of it:

     1. Animate the free-shipping bar from empty to full.
     2. Toggle the real "Insured Shipping" line item ($5.99) via
        Shopify's own /cart/add.js and /cart/change.js.
     3. Collapse Shopify's discount form behind a disclosure.
     4. Condense stacked variant options onto one line.

   Shopify's cart form, quantity inputs, remove buttons, discount
   submit and checkout button are never rewritten or intercepted.
   ============================================================ */
(function () {
  'use strict';

  var reduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* ---------- 1. Free shipping bar ---------------------------------- */
  /* The bar ships painted full so a visitor without JS never sees an
     empty meter. Arming it drops it to zero and lets the CSS transition
     carry it back up once it is on screen. */
  function shippingBar(scope) {
    $$('[data-sv-ship]', scope).forEach(function (bar) {
      if (bar._svBound) return;
      bar._svBound = true;

      if (reduced) return;                       // leave it full, no motion
      bar.setAttribute('data-sv-ship-armed', '');

      var fill = function () {
        // Two frames: one for the armed 0-width to paint, one to animate.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { bar.classList.add('is-full'); });
        });
      };

      if (!('IntersectionObserver' in window)) { fill(); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          fill();
        });
      }, { threshold: 0.5 });
      io.observe(bar);
    });
  }

  /* ---------- 2. Insured shipping ----------------------------------- */
  /* The add-on control is rendered twice — once in the cart page footer and
     once in the drawer — and Shopify only re-renders whichever section asked
     for the update. That let the two drift: the drawer could say "off" while
     the page still showed "on" from an earlier server render.

     So the cart itself is the single source of truth. After every change,
     and on every boot, both controls are set from /cart.js rather than from
     whatever the last render happened to bake in. */
  var syncPending = null;
  function syncInsured() {
    var boxes = $$('[data-sv-insured]');
    if (!boxes.length) return Promise.resolve();

    /* boot() runs from a MutationObserver, so this can be called many times
       in a burst. Coalesce them into one in-flight request. */
    if (syncPending) return syncPending;

    syncPending = fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = cart.items.filter(function (i) {
          return i.handle === 'insured-shipping';
        })[0];
        var on = !!line;

        boxes.forEach(function (b) {
          b.checked = on;
          var wrap = b.closest('.svins');
          if (wrap) wrap.classList.toggle('is-on', on);
        });

        /* Last line of defence. Insured shipping is a one-per-order thing,
           so if a cart is ever found carrying more than one — an older cart
           from before this guard, a stray add from anywhere else — quietly
           put it back to one rather than letting the customer be charged
           $5.99 twice for the same cover. */
        if (line && line.quantity > 1) {
          return request('/cart/change.js', { id: line.key, quantity: 1 })
            .then(refreshCart)
            .catch(function () {});
        }
      })
      .catch(function () { /* leave the controls as rendered */ })
      .then(function () { syncPending = null; });

    return syncPending;
  }

  function cartUrl(file) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + file;
  }

  /* Drive the cart to hold EXACTLY one insured-shipping line, or none.

     Never a blind /cart/add.js: that increments, so a customer who caught
     the drawer control and the page control before they had synced — or who
     double-tapped through a slow request — would have ended up with two
     lines and paid the $5.99 twice. Reading the cart first and converging
     on the target state makes the operation idempotent, so repeating it can
     only ever leave the same single line behind.

     `quantity: 1` on a line that is already 1 is a no-op to Shopify, and a
     line found at 2 or more is corrected back down rather than left. */
  function converge(variantId, wanted) {
    return fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = cart.items.filter(function (i) {
          return String(i.variant_id) === String(variantId);
        })[0];

        if (wanted) {
          if (!line) {
            return request('/cart/add.js', {
              items: [{ id: Number(variantId), quantity: 1 }]
            });
          }
          if (line.quantity !== 1) {
            return request('/cart/change.js', { id: line.key, quantity: 1 });
          }
          return null;                       // already exactly right
        }

        if (!line) return null;              // already gone
        return request('/cart/change.js', { id: line.key, quantity: 0 });
      });
  }

  /* Adds or removes a real line item. The checkbox is only moved to its
     new state after Shopify confirms the cart changed, so the control can
     never show "on" for a cart that does not actually carry the charge. */
  function insured(scope) {
    $$('[data-sv-insured]', scope).forEach(function (box) {
      if (box._svBound) return;
      box._svBound = true;

      box.addEventListener('change', function () {
        var wrap = box.closest('.svins');
        var wanted = box.checked;

        // Roll the checkbox back until the server agrees.
        box.checked = !wanted;
        if (wrap) wrap.classList.add('is-busy');

        var done = function (ok) {
          if (wrap) wrap.classList.remove('is-busy');
          if (!ok) {
            // Server refused: put every control back to what the cart says.
            syncInsured();
            return;
          }
          refreshCart();
          // Settle both copies against the real cart, not against `wanted`.
          syncInsured();
        };

        converge(box.dataset.variantId, wanted)
          .then(function () { done(true); }, function () { done(false); });
      });
    });
  }

  function request(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart request failed');
      return r.json();
    });
  }

  /* Re-render the cart through Shrine's own components so totals, the
     drawer and the header bubble all update the way they normally do. */
  function refreshCart() {
    var items = $('cart-items') || $('cart-drawer-items');
    if (items && typeof items.onCartUpdate === 'function') {
      items.onCartUpdate();
      return;
    }
    window.location.reload();
  }

  /* ---------- 3. Collapsible discount ------------------------------- */
  /* Shopify's <cart-discount-block> is moved wholesale inside a <details>.
     Its form, its submit button and its error paragraphs are the same
     nodes as before, so its own JS keeps working. */
  function discount(scope) {
    $$('cart-discount-block, .cart-discount', scope).forEach(function (block) {
      if (block._svBound) return;
      if (block.closest('.svdisc')) return;
      block._svBound = true;

      var details = document.createElement('details');
      details.className = 'svdisc';

      var summary = document.createElement('summary');
      summary.className = 'svdisc__sum';
      summary.textContent = 'Have a discount code?';

      var body = document.createElement('div');
      body.className = 'svdisc__body';

      block.parentNode.insertBefore(details, block);
      details.appendChild(summary);
      details.appendChild(body);
      body.appendChild(block);

      // If a code is already applied, the customer needs to see it.
      if (block.querySelector('.cart-discount__pill, [class*="discount__tag"]')) {
        details.open = true;
      }
    });
  }

  /* ---------- 4. Condensed variant options -------------------------- */
  /* "color: Black" + "Verison: 2.0" becomes "Black · 2.0" on one line.
     The original definition list is hidden rather than removed so screen
     readers still get the labelled name/value pairs. */
  function options(scope) {
    $$('.cart-item__details, .cart-drawer__item-details', scope).forEach(function (cell) {
      if (cell._svBound) return;
      cell._svBound = true;

      var opts = $$('.product-option', cell);
      if (!opts.length) return;

      /* Shrine reuses .product-option for more than the variant options —
         on the cart page the unit price is one too. So: anything carrying a
         colon is a real "name: value" option and contributes its value;
         anything else is only used if it isn't a bare money amount, and is
         split on "/" to unpack a combined variant title like "Black / 2.0"
         (which is the shape the drawer renders). */
      var values = [];
      var used = [];

      opts.forEach(function (o) {
        var txt = o.textContent.replace(/\s+/g, ' ').trim();
        if (!txt) return;

        var colon = txt.indexOf(':');
        if (colon !== -1) {
          var v = txt.slice(colon + 1).trim();
          if (v) { values.push(v); used.push(o); }
          return;
        }

        if (/^[^A-Za-z]*[\d][\d.,]*$/.test(txt)) return;   // a price, not an option

        var parts = txt.split('/');
        var got = false;
        parts.forEach(function (p) {
          p = p.trim();
          if (p) { values.push(p); got = true; }
        });
        if (got) used.push(o);
      });

      if (!values.length) return;

      var line = document.createElement('p');
      line.className = 'svopt';
      line.textContent = values.join(' · ');

      used[0].parentNode.insertBefore(line, used[0]);
      used.forEach(function (o) {
        o.hidden = true;
        o.classList.add('visually-hidden');
      });
    });
  }

  /* ---------- 5. WakeBand recommendation ------------------------------
     The button has to move the real cart, not the label above it, so both
     offers read /cart.js first and act on what Shopify actually holds.

     'upgrade' is a swap, because that is what the word promises: the Pro
     goes in at the quantity the standard band was at, and the standard
     lines then go to zero. The add runs first on purpose — if the second
     call fails, the customer is left holding more than they chose, which
     is visible in the summary and reversible, rather than less.

     'pair' raises the Pro line to exactly two, which is what trips the
     2-pack discount. Setting the quantity rather than adding again is
     what stops a double tap making it three. */
  function cartJson() {
    return fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function upsell(scope) {
    $$('[data-sv-upsell-add]', scope).forEach(function (btn) {
      if (btn._svBound) return;
      btn._svBound = true;

      btn.addEventListener('click', function () {
        if (btn.disabled) return;

        var host = btn.closest('[data-sv-upsell]');
        var label = btn.querySelector('[data-sv-upsell-label]');
        var note = host ? host.querySelector('[data-sv-upsell-note]') : null;
        var variantId = Number(btn.dataset.variantId);
        var offer = btn.dataset.offer;
        var swapHandle = btn.dataset.swapHandle;
        var qty = Math.max(1, Number(btn.dataset.qty) || 1);
        var was = label ? label.innerHTML : '';

        btn.disabled = true;
        if (host) host.classList.add('is-busy');
        if (label) label.textContent = 'Adding\u2026';

        function fail(err) {
          if (host) host.classList.remove('is-busy');
          btn.disabled = false;
          if (label) label.innerHTML = was;
          if (note) { note.hidden = false; note.textContent = 'That did not go through. Please try again.'; }
          if (window.console) console.warn('[sorven] upsell:', err && err.message);
        }

        cartJson()
          .then(function (cart) {
            var line = cart.items.filter(function (i) {
              return String(i.variant_id) === String(variantId);
            })[0];

            if (offer === 'pair') {
              if (line && line.quantity >= 2) return null;
              if (!line) return request(cartUrl('cart/add.js'), { items: [{ id: variantId, quantity: 2 }] });
              return request(cartUrl('cart/change.js'), { id: line.key, quantity: 2 });
            }

            var add = line
              ? request(cartUrl('cart/change.js'), { id: line.key, quantity: line.quantity + qty })
              : request(cartUrl('cart/add.js'), { items: [{ id: variantId, quantity: qty }] });

            return add
              .then(cartJson)
              .then(function (after) {
                var updates = {};
                var found = false;
                after.items.forEach(function (i) {
                  if (i.handle === swapHandle) { updates[i.key] = 0; found = true; }
                });
                if (!found) return null;
                return request(cartUrl('cart/update.js'), { updates: updates });
              });
          })
          .then(function () {
            /* The section re-render replaces this whole block, so there is
               nothing left here to reset. */
            refreshCart();
          })
          .catch(fail);
      });
    });
  }

  /* ---------- boot --------------------------------------------------- */
  function boot(scope) {
    shippingBar(scope);
    insured(scope);
    discount(scope);
    options(scope);
    upsell(scope);
    syncInsured();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else { boot(document); }

  // Shrine re-renders cart sections in place after every quantity change.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  var mo = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      if (muts[i].addedNodes.length) { boot(document); return; }
    }
  });
  if (document.body) {
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
